import posthog from 'posthog-js'
import { SSO_CLIENT } from '../../features/sso/types'

// PostHog stores what it receives outside Australia (EU region), so RxPad
// sends it no patient or clinician personal data (RXP-2552). With element
// text and attributes masked, autocapture only says "a button was clicked";
// these deliberate events say what happened (RXP-2553).
//
// Every event and every property it may carry is declared in ANALYTICS_EVENTS.
// trackEvent sends only those, and drops a property whose value is not of the
// declared kind. Properties are enums, counts, flags and error codes: never
// add one that can hold a name, search term, date of birth,
// Medicare/DVA/IHI number, medicine, email, phone number, SCID or free text.

type OneOf<T extends string> = { kind: 'oneOf'; values: readonly T[] }
type Count = { kind: 'count' }
type Flag = { kind: 'flag' }
type ErrorCode = { kind: 'errorCode' }
type Rule = OneOf<string> | Count | Flag | ErrorCode
type Optional<R extends Rule> = R & { optional: true }
type PropertyRule = Rule | Optional<Rule>

const oneOf = <T extends string>(...values: T[]): OneOf<T> => ({ kind: 'oneOf', values })
const count: Count = { kind: 'count' }
const flag: Flag = { kind: 'flag' }
const errorCode: ErrorCode = { kind: 'errorCode' }
const optional = <R extends Rule>(rule: R): Optional<R> => ({ ...rule, optional: true })

const result = oneOf('success', 'failed')
const ssoClient = oneOf(...Object.values(SSO_CLIENT))

// Names autocapture reports as the `action` property of a click. The
// data-ph-capture-attribute-* attribute is sent even with element attributes
// masked; plain data-* attributes are not. PostHog merges these from the
// clicked element's ancestors and the outermost wins, so tag the control
// itself and not a container around other tagged controls.
export const ANALYTICS_ACTIONS = [
  // Side nav, sign in and out
  'nav_new_script',
  'nav_patients',
  'nav_prescriptions',
  'nav_settings',
  'nav_data_reports',
  'nav_help',
  'sign_in',
  'sign_up',
  'sign_out',
  // New prescription
  'reset_script',
  'reset_script_confirm',
  'new_script_patient_search',
  'new_script_patient_select',
  'new_script_new_patient',
  'new_script_select_another_patient',
  'submit_script',
  'review_make_changes',
  'review_send',
  'send_results_abort',
  'send_results_print',
  'send_results_resend_token',
  'send_results_edit_failed',
  'send_results_close',
  // Patients and IHI
  'patients_search',
  'patients_new_patient',
  'patients_import',
  'patient_prescribe',
  'ihi_enter_manually',
  'ihi_back_to_medicare_dva',
  'ihi_validate',
  // Prescriptions
  'prescriptions_search',
  'prescription_actions_menu',
  'prescription_view',
  'prescription_print_eop',
  'prescription_check_dispense_status',
  'prescription_cease',
  'prescription_cease_confirm',
  'prescription_cancel',
  'prescription_cancel_confirm',
  'prescription_resend',
  'prescription_resend_confirm',
  'prescription_renew',
  'dispense_status_check_again',
  'dispense_status_cease',
  // Settings and reports
  'settings_tab_prescriber',
  'settings_tab_admin',
  'settings_tab_users',
  'settings_tab_organization',
  'settings_tab_locations',
  'settings_tab_subscription',
  'settings_tab_integrations',
  'integration_connect',
  'data_reports_tab_analytics',
  'data_reports_tab_exports',
] as const
export type AnalyticsAction = (typeof ANALYTICS_ACTIONS)[number]

export const analyticsAction = (action: AnalyticsAction) => ({
  'data-ph-capture-attribute-action': action,
})

const defineEvents = <T extends Record<string, Record<string, PropertyRule>>>(events: T): T =>
  events

export const ANALYTICS_EVENTS = defineEvents({
  // Every way the sign-in callback can end somewhere other than the app.
  auth_landing_exit: {
    reason: oneOf(
      'cognito_error',
      'linked_account_retry',
      'missing_code',
      'code_replayed',
      'launch_timeout_30s',
      'launch_failed',
      'incomplete_tokens',
      'exchange_failed',
      'no_user_id',
      'profile_load_error'
    ),
    // /sso: back through the SSO landing; which client is left out.
    to: oneOf('/', '/login', '/error', '/sso'),
    hadTokens: flag,
  },
  // client is absent for a sign-in that did not start at an SSO landing.
  signed_in: { client: optional(ssoClient) },
  signed_out: { mode: oneOf('full', 'ehr') },
  // fresh_login_hop: through Cognito /logout first, back to the landing after.
  sso_launch: {
    client: optional(ssoClient),
    result: oneOf('redirected', 'fresh_login_hop', 'unknown_client'),
  },
  ehr_launch: {
    result: oneOf('started', 'completed', 'failed'),
    reason: optional(
      oneOf(
        'disabled',
        'missing_launch_params',
        'launch_error',
        'no_token',
        'bootstrap_failed',
        'context_load_failed'
      )
    ),
  },
  patient_searched: { source: oneOf('new_script', 'patients_page'), resultCount: count },
  ihi_lookup: {
    method: oneOf('medicare', 'dva', 'ihi'),
    result: oneOf('found', 'not_found', 'error'),
    ihiStatus: optional(oneOf('Active', 'Deceased', 'Expired', 'Resolved', 'Retired')),
    recordStatus: optional(oneOf('Verified', 'Provisional', 'Unverified')),
  },
  script_sent: {
    itemCount: count,
    failedCount: count,
    deliveryMethod: oneOf('email', 'sms', 'paper_token', 'paper_prescription', 'none'),
  },
  // stage is the step that stopped the send: a pre-send check, the eRx
  // timeout, or the create request itself (api).
  script_send_failed: {
    stage: oneOf(
      'missing_data',
      'prescriber_patient_check',
      'drug_check',
      'hi_check',
      'scid_generation',
      'script_number',
      'timeout',
      'api'
    ),
    itemCount: count,
    errorCode: optional(errorCode),
  },
  prescription_cancelled: { result, errorCode: optional(errorCode) },
  prescription_reissued: {
    result,
    channel: oneOf('email', 'sms'),
    itemCount: count,
    errorCode: optional(errorCode),
  },
  prescription_renewed: {
    result: oneOf('success', 'failed', 'patient_mismatch'),
    errorCode: optional(errorCode),
  },
  prescription_ceased: { result, errorCode: optional(errorCode) },
  dispense_status_checked: {
    result,
    // The items' shared state, or mixed when they differ.
    status: optional(
      oneOf('Active', 'Pending', 'Dispensed', 'Cancelled', 'Deferred', 'Ceased', 'mixed')
    ),
    itemCount: optional(count),
    errorCode: optional(errorCode),
  },
  integration_connected: { result, errorCode: optional(errorCode) },
  // A tab chosen from the side panel's mobile picker, a <select> that
  // autocapture can't name. The desktop tabs are tagged clicks instead.
  tab_selected: { action: oneOf(...ANALYTICS_ACTIONS) },
})

type Events = typeof ANALYTICS_EVENTS
export type AnalyticsEvent = keyof Events

type ValueOf<R> = R extends OneOf<infer T>
  ? T
  : R extends Count
  ? number
  : R extends Flag
  ? boolean
  : R extends ErrorCode
  ? string | number
  : never
type RequiredKeys<S> = { [K in keyof S]: S[K] extends { optional: true } ? never : K }[keyof S]
type OptionalKeys<S> = Exclude<keyof S, RequiredKeys<S>>

export type AnalyticsProperties<E extends AnalyticsEvent> = {
  [K in RequiredKeys<Events[E]>]: ValueOf<Events[E][K]>
} & {
  [K in OptionalKeys<Events[E]>]?: ValueOf<Events[E][K]>
}

// An error code is a short number (404, 8003, 01613), an eRx code (E00025),
// or one of the symbolic codes below, which RxPad's API and axios return.
// Never a sentence: error messages can quote a patient's details. Symbolic
// codes are listed rather than matched, since a pattern that passes
// USER_NOT_FOUND_IN_INTEGRATION_PROVIDER passes JANE_CITIZEN too.
const NUMERIC_ERROR_CODE = /^[A-Z]?\d{1,5}$/
const SYMBOLIC_ERROR_CODES: readonly string[] = [
  'erx_rate_limited',
  'erx_unavailable',
  'dispense_status_check_in_progress',
  'dispense_status_changed',
  'USER_NOT_FOUND_IN_INTEGRATION_PROVIDER',
  'SERVER_NOT_RESPONDING',
  'MULTIPLE_SUBSCRIPTIONS',
  'WEBHOOK_NOT_READY',
  'ECONNABORTED',
  'ECONNRESET',
  'ETIMEDOUT',
  'ERR_NETWORK',
  'ERR_BAD_REQUEST',
  'ERR_BAD_RESPONSE',
  'ERR_CANCELED',
  // Set in RxPad itself
  'request_aborted',
  'missing_entity_id',
]
const UUID = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i

const isAllowed = (rule: PropertyRule, value: unknown): boolean => {
  switch (rule.kind) {
    case 'oneOf':
      return typeof value === 'string' && rule.values.includes(value)
    case 'count':
      return typeof value === 'number' && Number.isInteger(value) && value >= 0
    case 'flag':
      return typeof value === 'boolean'
    case 'errorCode':
      return (
        (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 99_999) ||
        (typeof value === 'string' &&
          (NUMERIC_ERROR_CODE.test(value) || SYMBOLIC_ERROR_CODES.includes(value)))
      )
  }
}

// Own keys only, so "constructor" or "toString" never reads as declared.
const ownValue = <T>(record: Record<string, T>, key: string): T | undefined =>
  Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined

const rulesFor = (event: string) => ownValue<Record<string, PropertyRule>>(ANALYTICS_EVENTS, event)

export const sanitizeProperties = (
  event: string,
  properties: Record<string, unknown>
): Record<string, unknown> => {
  const rules = rulesFor(event)
  if (!rules) return {}
  const sanitized: Record<string, unknown> = {}
  Object.entries(properties).forEach(([key, value]) => {
    const rule = ownValue(rules, key)
    if (rule && isAllowed(rule, value)) sanitized[key] = value
    else if (process.env.NODE_ENV !== 'production' && value !== undefined) {
      console.warn(`analytics: dropped ${event}.${key}, which is not declared or not allowed`)
    }
  })
  return sanitized
}

export function trackEvent<E extends AnalyticsEvent>(
  event: E,
  ...[properties]: RequiredKeys<Events[E]> extends never
    ? [properties?: AnalyticsProperties<E>]
    : [properties: AnalyticsProperties<E>]
): void {
  if (!rulesFor(event)) return
  posthog.capture(event, sanitizeProperties(event, (properties ?? {}) as Record<string, unknown>))
}

// routes types its paths as string; one outside the declared list is dropped.
export const trackAuthLandingExit = (
  reason: AnalyticsProperties<'auth_landing_exit'>['reason'],
  to: string,
  hadTokens: boolean
): void =>
  trackEvent('auth_landing_exit', {
    reason,
    to: to as AnalyticsProperties<'auth_landing_exit'>['to'],
    hadTokens,
  })

// signed_out goes out as the clinician who signed out; then PostHog forgets
// them. Otherwise, on a shared clinic computer, everything until the next
// clinician's identify() -- the logged-out page, the sign-in -- is recorded
// against this clinician. Clearing localStorage on sign-out does not do it:
// PostHog keeps the distinct id in a cookie too.
//
// A new device id as well: the current one is the anonymous id this clinician
// had before signing in, which their identify() made one of their ids.
export const trackSignedOut = (mode: AnalyticsProperties<'signed_out'>['mode']): void => {
  trackEvent('signed_out', { mode })
  posthog.reset(true)
}

// The code an API error carries, for an errorCode property. The rule above
// drops it if it turns out not to look like a code.
export const errorCodeOf = (error: unknown): string | number | undefined => {
  if (!error || typeof error !== 'object') return undefined
  const { name, code, status, data, message } = error as Record<string, unknown>
  // The second is what useBatchMutation throws when a batch is aborted.
  if (name === 'AbortError' || (error instanceof Error && message === 'Request aborted')) {
    return 'request_aborted'
  }
  if (typeof code === 'string' || typeof code === 'number') return code
  if (data && typeof data === 'object') {
    const nested = errorCodeOf(data)
    if (nested !== undefined) return nested
  }
  if (typeof status === 'number') return status
  return undefined
}

// Tags every later event, autocapture included, with the signed-in
// organisation's internal id, so an issue can be narrowed to one organisation.
// Group analytics would do this too, but is a paid PostHog add-on.
export const ORGANIZATION_PROPERTY = 'organizationId'

export const setAnalyticsOrganization = (organizationId: string | undefined): void => {
  if (organizationId && UUID.test(organizationId)) {
    posthog.register({ [ORGANIZATION_PROPERTY]: organizationId })
  } else {
    posthog.unregister(ORGANIZATION_PROPERTY)
  }
}

interface OrganizationStore {
  getState: () => { organization?: { data?: { id?: string } } }
  subscribe: (listener: () => void) => () => void
}

// Mirrors the store's organisation on every dispatch, so an event captured
// straight after the organisation loads, changes or clears (sign-out, an
// invalidated context) already carries the right id; a React effect would run
// after the components that capture it. The first sync also clears an id
// PostHog kept in the browser from an earlier session, which the next user of
// a shared clinic computer would otherwise inherit.
export const followAnalyticsOrganization = (store: OrganizationStore): (() => void) => {
  let current: string | undefined | null = null
  const sync = () => {
    const organizationId = store.getState().organization?.data?.id
    if (organizationId === current) return
    current = organizationId
    setAnalyticsOrganization(organizationId)
  }
  sync()
  return store.subscribe(sync)
}
